/**
 * EmailR — email address field.
 * Handles type: email
 */

import { useFormContext } from 'react-hook-form';
import { TextField } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { buildValidationRules } from '../validation/fromSchema';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function EmailR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {value ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <TextField
      name={name}
      label={label}
      hint={resolveHelpText(field, audience)}
      required={field.required}
      rules={buildValidationRules(field)}
      type="email"
      placeholder={field.example ?? 'you@example.com'}
      tooltip={lookupFieldTooltip(field.canonical_name)}
    />
  );
}
